import { SettingsQuery } from '@state/settings/settings.query';
import {ClinicQuery} from '@state/clinic/clinic.query';
import {ClinicService} from '@state/clinic/clinic.service';
import {SettingsService} from '@state/settings/settings.service';
import {UiService} from '@state/ui/ui.service';
import {ApiErrorLogService} from '@core/services/api-error-log/api-error-log.service';
import {SESSION_ERROR_IDS} from '@core/constants';
import jsonPackage from '../../../../../package.json';
import {Injectable} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {Capacitor} from '@capacitor/core';
import {Observable, of, TimeoutError} from 'rxjs';
import {finalize, shareReplay, switchMap, tap} from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class InitFlowwwService {
    private initInFlight$: Observable<void> | null = null;

    constructor(
        private clinicQuery: ClinicQuery,
        private clinicService: ClinicService,
        private settingsQuery: SettingsQuery,
        private settingsService: SettingsService,
        private uiService: UiService,
        private apiErrorLogService: ApiErrorLogService
    ) {}

    get initInProgress(): boolean {
        return this.initInFlight$ !== null;
    }

    init(): Observable<void> {
        // settingsLoaded es la única fuente de verdad: systemLogout lo resetea,
        // así que tras un re-login la inicialización vuelve a ejecutarse.
        // No basta por sí solo: se commitea ANTES de initClinic, así que si
        // initClinic falló la clínica activa queda a medias (sin cabinets ni
        // horarios, loaded !== true) y el grid de la agenda no podría cargar
        // nunca — en ese caso hay que reintentar
        if (this.settingsQuery.settingsLoaded && this.clinicQuery.getActive()?.loaded) {
            return of(void 0);
        }

        // Compartir la petición en vuelo: app.component y las páginas (vía
        // InitFlowwwPromiseService) pueden pedir la inicialización a la vez y
        // solo debe lanzarse un SEC_FLOWWWME_INIT_FORM
        if (!this.initInFlight$) {
            // Con settings ya cargados solo falta la clínica: reintentar
            // únicamente initClinic evita repetir el init completo y sus
            // efectos (alta de clínicas, toasts, labels)
            const init$ = this.settingsQuery.settingsLoaded && this.clinicQuery.getActive()
                ? this.clinicService.initClinic()
                : this.settingsService.initFlowwwMe().pipe(
                    switchMap(() => this.clinicService.initClinic())
                );

            // Que el detalle no enseñe el fallo de un intento anterior
            this.apiErrorLogService.clear();

            this.initInFlight$ = init$.pipe(
                // Sin init no hay labels, clínicas ni menú y ninguna página vuelve
                // a intentarlo sola: un fallo de red dejaría la app en blanco sin
                // salida. Los errores de sesión ya acaban en /login vía DataInterceptor
                tap({
                    next: () => this.uiService.setInitError(null),
                    error: err => {
                        if (!SESSION_ERROR_IDS.includes(err) && err !== 'system_reload') {
                            this.uiService.setInitError(this.describeError(err));
                        }
                    }
                }),
                finalize(() => this.initInFlight$ = null),
                shareReplay({ bufferSize: 1, refCount: false })
            );
        }

        return this.initInFlight$;
    }

    /**
     * Un status 0 no dice si es falta de red, CORS, DNS o TLS: el estado
     * online y la URL ayudan a soporte a distinguirlos
     */
    private describeError(err: unknown): string {
        const lines = [
            new Date().toISOString(),
            `v${jsonPackage.version} · ${Capacitor.getPlatform()} · ${navigator.onLine ? 'online' : 'offline'}`
        ];

        if (err instanceof HttpErrorResponse) {
            lines.push(`HTTP ${err.status} ${err.statusText}`, err.url ?? '');
        } else if (err instanceof TimeoutError) {
            lines.push('Timeout');
        } else if (typeof err === 'number') {
            lines.push(`FLOWww error ${err}`);
        } else {
            lines.push(String((err as Error)?.message ?? err));
        }

        // Solo el response: el body de la petición puede llevar el PIN
        const failed = this.apiErrorLogService.last;
        if (failed) {
            lines.push('', `${failed.form} · HTTP ${failed.status}`, this.readableBody(failed.body).slice(0, 2000) || '(no response body)');
        }

        return lines.join('\n');
    }

    /**
     * El XML de FLOWww se enseña tal cual, pero una página de error del IIS
     * se reduce a su texto: el HTML crudo es ilegible en la pantalla del móvil
     */
    private readableBody(body: string): string {
        if (body.includes('<FLOWww_AES')) {
            return body;
        }

        return body
            .replace(/<(script|style)[\s\S]*?<\/\1>/gi, '')
            .replace(/<[^>]+>/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
    }
}
